/**
 * Cash App integrations for express-checkout.js
 * - CashAppPay: Cash App Pay flow (via Afterpay SDK)
 * - Afterpay: Standard Afterpay flow
 */

class CashAppBase {
	constructor(opts = {}) {
		this.countryCode = opts.countryCode || 'US';
		this.token = opts.token || undefined;
		this.returnUrl = opts.returnUrl || undefined;
		this.cancelUrl = opts.cancelUrl || undefined;
	}

	_ap() {
		const ap = (typeof window !== 'undefined') ? window.AfterPay : undefined;
		if (!ap) {
			console.error('[Afterpay] SDK not found on window.AfterPay');
			return null;
		}
		return ap;
	}

	_toQuery(params) {
		return new URLSearchParams(params).toString();
	}

	_redirectToReturn(status, token, cashtag) {
		const query = this._toQuery({
			status: String(status ?? ''),
			orderToken: String(token ?? ''),
			cashtag: String(cashtag ?? ''),
		});
		window.location.assign([this.returnUrl, '?', query].join(''));
	}
}

/**
 * Cash App Pay
 */
export class CashAppPay extends CashAppBase {
	/**
	 * Initialize Cash App Pay-specific Afterpay integration
	 * Sets up the onComplete handler that receives status and orderToken.
	 */
	initializeForCashAppPay() {
		const ap = this._ap();
		if (!ap) return false;

		const cashAppPayOptions = {
			onComplete: (event) => {
				const {status, orderToken, cashtag} = event?.data ?? {};
				this._redirectToReturn(status, orderToken, cashtag);
			}
		};

		ap.initializeForCashAppPay({
			countryCode: this.countryCode,
			token: this.token,
			cashAppPayOptions
		});

		return true;
	}

	/**
	 * Renders the Cash App Pay button and begins the flow.
	 * Call with a server-generated token.
	 */
	open() {
		const ap = this._ap();
		if (!ap) return false;

		if (!this.token) {
			console.error('[CashAppPay] Missing token');
			return false;
		}

		const cashAppPayButtonOptions = {
			button: false,
			manage: false,
			onBegin: ({ begin }) => {
				this.initializeForCashAppPay();
				begin();
			},
		};

		ap.renderCashAppPayButton({
			countryCode: this.countryCode,
			cashAppPayButtonOptions,
		});

		return true;
	}
}

/**
 * Standard Afterpay (non–Cash App Pay)
 */
export class Afterpay extends CashAppBase {
	/**
	 * Starts a normal Afterpay redirect flow.
	 * Call with a server-generated token.
	 */
	open() {
		const ap = this._ap();
		if (!ap) return false;

		let token = this.token;
		if (!token) {
			console.error('[Afterpay] Missing token');
			return false;
		}

		ap.initialize({ countryCode: this.countryCode });
		ap.redirect({ token });
		return true;
	}
}

/**
 * Simple factory
 */
const cashApp = {
	getInstance(opts){
		return opts.isCashAppPay
			? new CashAppPay(opts)
			: new Afterpay(opts);
	}
};

export default cashApp;
